import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useLocation } from 'react-router-dom';
import {
  SETTINGS_ROUTES,
  DEFAULT_MENU_ROUTE,
  SettingsMenuItem,
} from 'routes/settings.config';
import { UserPermissionsPerCompanyResponse } from 'types/rolesTypes';
import { UserCompany } from 'types/userTypes';
import { ROUTES } from 'routes/routes';

export enum SelectedMenu {
  HOME = 'HOME',
  SETTINGS = 'SETTINGS',
}

const STORAGE_KEYS = {
  SELECTED_COMPANY: 'selectedCompany',
  SELECTED_DARWIN_USER: 'darwin_user_email',
  SELECTED_AUTH_STORE: 'auth_store',
} as const;

const NON_SETTINGS_ROUTES: Record<string, SelectedMenu> = {
  [ROUTES.HOME]: SelectedMenu.HOME,
};

export const getMenuFromPath = (path: string): SelectedMenu =>
  NON_SETTINGS_ROUTES[path] ??
  (SETTINGS_ROUTES[path] ? SelectedMenu.SETTINGS : SelectedMenu.HOME);

export const getSettingsItemFromPath = (path: string): SettingsMenuItem =>
  SETTINGS_ROUTES[path] ?? DEFAULT_MENU_ROUTE;

export type UIContextType = {
  selectedMenu: SelectedMenu;
  selectedCompany: string | null;
  selectedSettingsMenu: SettingsMenuItem;
  userPermissionsPerCompany: UserPermissionsPerCompanyResponse;
  userPrimaryCompany: UserCompany | undefined;
  setSelectedMenu: (menu: SelectedMenu) => void;
  setSelectedCompany: (companyId: string) => void;
  setSelectedSettingMenu: (menu: SettingsMenuItem) => void;
  setUserPermissionsPerCompany: (
    permissions: UserPermissionsPerCompanyResponse
  ) => void;
  setUserPrimaryCompany: (primaryCompany: UserCompany | undefined) => void;
  clearUIState: () => void;
};

export const UIContext = createContext<UIContextType>({
  selectedMenu: SelectedMenu.HOME,
  selectedCompany: null,
  selectedSettingsMenu: DEFAULT_MENU_ROUTE,
  userPermissionsPerCompany: {},
  userPrimaryCompany: undefined,
  setSelectedMenu: () => {},
  setSelectedCompany: () => {},
  setSelectedSettingMenu: () => {},
  setUserPermissionsPerCompany: () => {},
  setUserPrimaryCompany: () => {},
  clearUIState: () => {},
});

type UIProviderProps = {
  children: ReactNode;
};

export const UIProvider = ({ children }: UIProviderProps) => {
  const location = useLocation();

  const [selectedMenu, setSelectedMenu] = useState<SelectedMenu>(
    getMenuFromPath(location.pathname)
  );

  const [selectedCompany, setSelectedCompany] = useLocalStorage<string | null>(
    STORAGE_KEYS.SELECTED_COMPANY,
    null
  );

  const [selectedSettingsMenu, setSelectedSettingMenu] =
    useState<SettingsMenuItem>(DEFAULT_MENU_ROUTE);

  const [userPermissionsPerCompany, setUserPermissionsPerCompany] =
    useState<UserPermissionsPerCompanyResponse>({});

  const [userPrimaryCompany, setUserPrimaryCompany] = useState<
    UserCompany | undefined
  >(undefined);

  const clearUIState = useCallback(() => {
    Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
    setSelectedMenu(SelectedMenu.HOME);
    setSelectedCompany(null);
    setSelectedSettingMenu(DEFAULT_MENU_ROUTE);
  }, [setSelectedCompany]);

  useEffect(() => {
    const menuFromPath = getMenuFromPath(location.pathname);
    setSelectedMenu(menuFromPath);

    if (menuFromPath === SelectedMenu.SETTINGS) {
      setSelectedSettingMenu(getSettingsItemFromPath(location.pathname));
    } else {
      setSelectedSettingMenu(DEFAULT_MENU_ROUTE);
    }
  }, [location.pathname]);

  const handleSetSelectedCompany = useCallback(
    (companyId: string) => setSelectedCompany(companyId),
    [setSelectedCompany]
  );

  const handleSetSelectedMenu = useCallback((menu: SelectedMenu) => {
    setSelectedMenu(menu);
    if (menu !== SelectedMenu.SETTINGS) {
      setSelectedSettingMenu(DEFAULT_MENU_ROUTE);
    }
  }, []);

  const handleSetSelectedSettingMenu = useCallback(
    (menu: SettingsMenuItem) => setSelectedSettingMenu(menu),
    []
  );

  const handleSetUserPermissionsPerCompany = useCallback(
    (permissions: UserPermissionsPerCompanyResponse) =>
      setUserPermissionsPerCompany(permissions),
    []
  );

  const handleSetUserPrimaryCompany = useCallback(
    (primaryCompany: UserCompany | undefined) =>
      setUserPrimaryCompany(primaryCompany),
    []
  );

  return (
    <UIContext.Provider
      value={{
        selectedMenu,
        selectedCompany,
        selectedSettingsMenu,
        userPermissionsPerCompany,
        userPrimaryCompany,
        setSelectedMenu: handleSetSelectedMenu,
        setSelectedCompany: handleSetSelectedCompany,
        setSelectedSettingMenu: handleSetSelectedSettingMenu,
        setUserPermissionsPerCompany: handleSetUserPermissionsPerCompany,
        setUserPrimaryCompany: handleSetUserPrimaryCompany,
        clearUIState,
      }}
    >
      {children}
    </UIContext.Provider>
  );
};

export const useUI = (): UIContextType => {
  const context = useContext(UIContext);
  if (!context) throw new Error('useUI must be used within a UIProvider');
  return context;
};
